import React, { useState, useEffect } from "react";
import EmploymentForm from "./EmploymentForm";
import EmploymentCard from "./EmploymentCard";
import { sectionsAPI } from "../../../services/sectionsAPI";
import { formatAPIError } from "../../../utils/validation";
import "./styles/EmploymentSection.css";

const EmploymentSection = () => {
  const [employments, setEmployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(null);

  // custom delete confirmation (same as EducationSection)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadEmployments();
  }, []);

  const loadEmployments = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await sectionsAPI.employment.getAll();
      setEmployments(response.data || []);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployment = () => {
    setEditingEmployment(null);
    setIsFormOpen(true);
  };

  const handleEditEmployment = (employment) => {
    setEditingEmployment(employment);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      if (editingEmployment) {
        await sectionsAPI.employment.update(editingEmployment.id, formData);
        setSuccess("✅ Employment updated successfully!");
      } else {
        await sectionsAPI.employment.add(formData);
        setSuccess("✅ Employment added successfully!");
      }

      await loadEmployments();
      setIsFormOpen(false);
      setEditingEmployment(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // open custom confirm dialog instead of window.confirm
  const askDeleteEmployment = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      setError("");
      setSuccess("");
      await sectionsAPI.employment.delete([deleteId]);
      setSuccess("✅ Employment deleted successfully!");
      await loadEmployments();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatAPIError(err));
    } finally {
      closeConfirm();
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingEmployment(null);
  };

  if (loading) {
    return (
      <div className="employment-section">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading employments...</p>
          <p className="loading-subtext">
            Start building your professional profile by adding your work experience and career history.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="employment-section">
      {/* Header (matches Education header pattern) */}
      <div className="employment-header">
        <h2 className="employment-heading">Employment</h2>
        <button
          type="button"
          className="employment-add-btn"
          onClick={handleAddEmployment}
        >
          <span className="employment-add-plus">+</span>
          Add Employment
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>✅</span>
          <span>{success}</span>
        </div>
      )}

      {/* Empty state */}
      
        <div className="employment-list">
          {employments.map((emp) => (
            <EmploymentCard
              key={emp.id}
              employment={emp}
              onEdit={() => handleEditEmployment(emp)}
              onDelete={() => askDeleteEmployment(emp.id)}
            />
          ))}
        </div>
      

      {/* Form modal */}
      {isFormOpen && (
        <EmploymentForm
          employment={editingEmployment}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          loading={formLoading}
        />
      )}

      {/* Custom confirmation dialog (same style as EducationSection) */}
      {confirmOpen && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">Delete employment</h3>
            <p className="confirm-body">
              This action cannot be undone. This will permanently delete this employment entry.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-cancel"
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmploymentSection;
